import { useCallback, useEffect, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { getApiErrorMessage } from '../../api/http';
import {
  createCustomEpgChannel,
  createCustomEpgEvent,
  deleteCustomEpgChannel,
  deleteCustomEpgEvent,
  fetchCustomEpgChannels,
  fetchCustomEpgEvents,
  notifyEpgSourcesUpdated,
  type EpgCustomChannelRow,
  type EpgCustomEventRow,
  type EpgCustomRepeatMode,
} from '../../api/epgApi';
import { useToast } from '../../context/ToastContext';

function toDatetimeLocalValue(isoOrSql: string): string {
  const d = new Date(isoOrSql);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(value: string): string {
  if (!value.trim()) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

function defaultEventTimes(): { start: string; end: string } {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    start: toDatetimeLocalValue(start.toISOString()),
    end: toDatetimeLocalValue(end.toISOString()),
  };
}

export default function EpgCreatorModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();

  const [channels, setChannels] = useState<EpgCustomChannelRow[]>([]);
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [events, setEvents] = useState<EpgCustomEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelId, setNewChannelId] = useState('');

  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const [eventRepeat, setEventRepeat] = useState<EpgCustomRepeatMode>('none');

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCustomEpgChannels();
      if (!res.success) {
        showToast(res.message || 'Failed to load custom channels', 'error');
        setChannels([]);
        return;
      }
      setChannels(Array.isArray(res.data) ? res.data : []);
      setSourceId(res.source_id ?? null);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to load custom channels'), 'error');
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadEvents = useCallback(
    async (channelId: number) => {
      try {
        const res = await fetchCustomEpgEvents(channelId);
        if (!res.success) {
          showToast(res.message || 'Failed to load events', 'error');
          setEvents([]);
          return;
        }
        setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        showToast(getApiErrorMessage(e, 'Failed to load events'), 'error');
        setEvents([]);
      }
    },
    [showToast],
  );

  useEffect(() => {
    if (!show) {
      setSelectedChannelId(null);
      setEvents([]);
      setNewChannelName('');
      setNewChannelId('');
      setEventTitle('');
      setEventDescription('');
      setEventRepeat('none');
      const defaults = defaultEventTimes();
      setEventStart(defaults.start);
      setEventEnd(defaults.end);
      return;
    }
    void loadChannels();
    const defaults = defaultEventTimes();
    setEventStart(defaults.start);
    setEventEnd(defaults.end);
  }, [loadChannels, show]);

  useEffect(() => {
    if (!show || selectedChannelId == null) {
      setEvents([]);
      return;
    }
    void loadEvents(selectedChannelId);
  }, [loadEvents, selectedChannelId, show]);

  const onCreateChannel = useCallback(async () => {
    const name = newChannelName.trim();
    const channelId = newChannelId.trim();
    if (!name || !channelId) {
      showToast('Channel name and ID are required', 'warning');
      return;
    }
    setBusy(true);
    try {
      const res = await createCustomEpgChannel({ name, channel_id: channelId });
      if (!res.success) {
        showToast(res.message || 'Failed to create channel', 'error');
        return;
      }
      showToast('Custom EPG channel created', 'success');
      notifyEpgSourcesUpdated();
      setNewChannelName('');
      setNewChannelId('');
      await loadChannels();
      if (res.id) setSelectedChannelId(res.id);
      if (res.source_id) setSourceId(res.source_id);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to create channel'), 'error');
    } finally {
      setBusy(false);
    }
  }, [loadChannels, newChannelId, newChannelName, showToast]);

  const onDeleteChannel = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this custom EPG channel and all its events?')) return;
      setBusy(true);
      try {
        const res = await deleteCustomEpgChannel(id);
        if (!res.success) {
          showToast(res.message || 'Failed to delete channel', 'error');
          return;
        }
        showToast('Channel deleted', 'success');
        if (selectedChannelId === id) setSelectedChannelId(null);
        await loadChannels();
      } catch (e) {
        showToast(getApiErrorMessage(e, 'Failed to delete channel'), 'error');
      } finally {
        setBusy(false);
      }
    },
    [loadChannels, selectedChannelId, showToast],
  );

  const onCreateEvent = useCallback(async () => {
    if (selectedChannelId == null) {
      showToast('Select a custom channel first', 'warning');
      return;
    }
    const title = eventTitle.trim();
    const startIso = datetimeLocalToIso(eventStart);
    const endIso = datetimeLocalToIso(eventEnd);
    if (!title || !startIso || !endIso) {
      showToast('Title, start and end time are required', 'warning');
      return;
    }
    setBusy(true);
    try {
      const res = await createCustomEpgEvent({
        custom_channel_id: selectedChannelId,
        title,
        description: eventDescription.trim() || undefined,
        start_time: startIso,
        end_time: endIso,
        repeat_mode: eventRepeat,
      });
      if (!res.success) {
        showToast(res.message || 'Failed to create event', 'error');
        return;
      }
      showToast('Event created and materialized to EPG data', 'success');
      setEventTitle('');
      setEventDescription('');
      setEventRepeat('none');
      const defaults = defaultEventTimes();
      setEventStart(defaults.start);
      setEventEnd(defaults.end);
      await loadEvents(selectedChannelId);
      notifyEpgSourcesUpdated();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to create event'), 'error');
    } finally {
      setBusy(false);
    }
  }, [
    eventDescription,
    eventEnd,
    eventRepeat,
    eventStart,
    eventTitle,
    loadEvents,
    selectedChannelId,
    showToast,
  ]);

  const onDeleteEvent = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this event?')) return;
      setBusy(true);
      try {
        const res = await deleteCustomEpgEvent(id);
        if (!res.success) {
          showToast(res.message || 'Failed to delete event', 'error');
          return;
        }
        showToast('Event deleted', 'success');
        if (selectedChannelId != null) await loadEvents(selectedChannelId);
      } catch (e) {
        showToast(getApiErrorMessage(e, 'Failed to delete event'), 'error');
      } finally {
        setBusy(false);
      }
    },
    [loadEvents, selectedChannelId, showToast],
  );

  const selectedChannel = channels.find((c) => c.id === selectedChannelId) ?? null;

  return (
    <SimpleModal
      show={show}
      title="EPG Creator"
      onClose={onClose}
      size="xl"
      footer={
        <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
          Close
        </button>
      }
    >
      <p className="text-muted small mb-3">
        Create your own EPG channels and events. They appear in <strong>Select EPG Source</strong>{' '}
        as <em>Custom EPG</em>
        {sourceId ? ` (source #${sourceId})` : ''}. Events are materialized for the next 7 days.
      </p>

      <div className="row g-3">
        <div className="col-md-5">
          <h6 className="fw-semibold">Custom channels</h6>
          {loading ? (
            <p className="text-muted small">Loading…</p>
          ) : channels.length === 0 ? (
            <p className="text-muted small">No custom channels yet.</p>
          ) : (
            <div className="list-group mb-3">
              {channels.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center${
                    selectedChannelId === ch.id ? ' active' : ''
                  }`}
                  onClick={() => setSelectedChannelId(ch.id)}
                  disabled={busy}
                >
                  <span>
                    <span className="fw-medium">{ch.name}</span>
                    <br />
                    <span className="small opacity-75">{ch.channel_id}</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      void onDeleteChannel(ch.id);
                    }}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </button>
              ))}
            </div>
          )}

          <h6 className="fw-semibold">Create channel</h6>
          <div className="mb-2">
            <label className="form-label small">Name</label>
            <input
              className="form-control form-control-sm"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder="My PPV"
              disabled={busy}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small">EPG ID (tvg-id)</label>
            <input
              className="form-control form-control-sm"
              value={newChannelId}
              onChange={(e) => setNewChannelId(e.target.value)}
              placeholder="my-ppv"
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => void onCreateChannel()}
            disabled={busy}
          >
            Create channel
          </button>
        </div>

        <div className="col-md-7">
          <h6 className="fw-semibold">
            Events{selectedChannel ? ` — ${selectedChannel.name}` : ''}
          </h6>
          {!selectedChannel ? (
            <p className="text-muted small">Select a channel to manage events.</p>
          ) : (
            <>
              {events.length === 0 ? (
                <p className="text-muted small">No events for this channel.</p>
              ) : (
                <div className="table-responsive mb-3">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Start</th>
                        <th>Repeat</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => (
                        <tr key={ev.id}>
                          <td>
                            <div className="fw-medium">{ev.title}</div>
                            {ev.description ? (
                              <div className="small text-muted">{ev.description}</div>
                            ) : null}
                          </td>
                          <td className="small text-nowrap">
                            {toDatetimeLocalValue(ev.start_time).replace('T', ' ')}
                          </td>
                          <td className="small">{ev.repeat_mode.replace('_', ' ')}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => void onDeleteEvent(ev.id)}
                              disabled={busy}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h6 className="fw-semibold">Create event</h6>
              <div className="mb-2">
                <label className="form-label small">Title</label>
                <input
                  className="form-control form-control-sm"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Description</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={2}
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="row g-2 mb-2">
                <div className="col-sm-6">
                  <label className="form-label small">Start</label>
                  <input
                    type="datetime-local"
                    className="form-control form-control-sm"
                    value={eventStart}
                    onChange={(e) => setEventStart(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label small">End</label>
                  <input
                    type="datetime-local"
                    className="form-control form-control-sm"
                    value={eventEnd}
                    onChange={(e) => setEventEnd(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label small">Repeat</label>
                <select
                  className="form-select form-select-sm"
                  value={eventRepeat}
                  onChange={(e) => setEventRepeat(e.target.value as EpgCustomRepeatMode)}
                  disabled={busy}
                >
                  <option value="none">None</option>
                  <option value="every_2h">Every 2 hours</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => void onCreateEvent()}
                disabled={busy}
              >
                Create event
              </button>
            </>
          )}
        </div>
      </div>
    </SimpleModal>
  );
}
